/**
 * Pure matchers for the LOCAL column filters of the pipeline board
 * (modal "Filtrar itens: <coluna>" in PipelineColumn.vue).
 *
 * Field-shape contract (serialize_card_for_index — pipeline_cards_controller.rb):
 * - `priority` lives at the card ROOT (string column, default 'none', NOT NULL).
 *   `item_details.priority` is a legacy mirror kept only as fallback.
 * - Card value follows the server-side filter semantics
 *   (PipelineCard::VALUE_NUMERIC_SQL): COALESCE(expected_revenue,
 *   item_details.value::numeric). Valueless cards never match a value
 *   filter (NULL-rejecting predicates), mirroring `with_value_between`.
 * - The assigned agent is `owner_id` at the ROOT. The legacy candidates
 *   (assigned_agent_id, assignee_id, item_details.agent_id, meta.agent_id)
 *   are kept as fallback for old payload shapes.
 *
 * Bugfix 2026-06-10: the original inline logic read item_details.priority /
 * item_details.value and never looked at owner_id, so priority/agent filters
 * matched 0 cards on current payloads (origin commit d8d04e5c18c).
 */

import { formatInReportingTimezone } from '../utils/localeFormatting';

export const getCardPriority = card =>
  card?.priority ?? card?.item_details?.priority ?? 'none';

// JS mirror of PipelineCard::NUMERIC_VALUE_REGEXP — full-string match only.
// parseFloat would accept '123abc'/'1,5' locally while the server rejects
// them (parity break flagged by cross-review).
const NUMERIC_VALUE_REGEXP = /^[+-]?(\d{1,18}(\.\d{1,6})?|\.\d{1,6})$/;

const parseNumeric = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return NUMERIC_VALUE_REGEXP.test(trimmed) ? Number(trimmed) : null;
};

export const getCardValue = card => {
  const revenue = parseNumeric(card?.expected_revenue);
  if (revenue !== null) return revenue;

  return parseNumeric(card?.item_details?.value);
};

// Fase 59 — valor REALIZADO: a soma das oportunidades registradas no card.
// Só existe quando houve pelo menos uma venda; um card que nunca vendeu
// devolve null e continua sendo exibido pelo valor PREVISTO (getCardValue).
// Os dois nunca se misturam num número só: somar previsto com realizado
// inflaria a previsão de receita com dinheiro que já entrou.
export const getCardRealizedValue = card => {
  const count = Number(card?.opportunities_count ?? 0);
  if (!Number.isFinite(count) || count <= 0) return null;

  return parseNumeric(card?.opportunities_total);
};

export const getCardOpportunitiesCount = card => {
  const count = Number(card?.opportunities_count ?? 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

// Quantas entradas o histórico tem no total, INCLUSIVE estornadas. É o que
// decide se o card oferece acesso ao histórico: usar a contagem de registradas
// faria a trilha de auditoria sumir da tela ao estornar a última venda, e um
// card com histórico ficaria indistinguível de um que nunca vendeu.
export const getCardOpportunitiesHistoryCount = card => {
  const count = Number(
    card?.opportunities_history_count ?? card?.opportunities_count ?? 0
  );
  return Number.isFinite(count) && count > 0 ? count : 0;
};

// Detail surfaces preserve the explicitly edited JSONB value over the derived
// forecast revenue. This differs intentionally from server filter semantics,
// which use `getCardValue` above (expected_revenue first).
export const getEffectiveCardValue = card => {
  const manualValue = card?.item_details?.value;
  if (manualValue !== null && manualValue !== undefined && manualValue !== '') {
    return manualValue;
  }
  const revenue = card?.expected_revenue;
  return revenue !== null && revenue !== undefined && revenue !== ''
    ? revenue
    : null;
};

const normalizeCurrencyCode = value => {
  const raw =
    value !== null && typeof value === 'object'
      ? value.code || value.iso_code || value.value
      : value;
  const code = String(raw || '')
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
};

export const getCardCurrency = (card, fallback = 'BRL') =>
  normalizeCurrencyCode(card?.currency) ||
  normalizeCurrencyCode(card?.item_details?.currency) ||
  normalizeCurrencyCode(fallback) ||
  'BRL';

export const getCardAgentId = card => {
  // Root owner_id is AUTHORITATIVE when the key exists — a card unassigned
  // today (owner_id: null) must not resurrect a stale legacy agent field.
  if (card && 'owner_id' in card) {
    const id = Number(card.owner_id);
    return card.owner_id == null || Number.isNaN(id) ? null : id;
  }

  // Legacy payload shapes only (no owner_id key at all).
  const candidates = [
    card?.assigned_agent_id,
    card?.assignee_id,
    card?.item_details?.agent_id,
    card?.meta?.agent_id,
  ];
  const found = candidates.find(candidate => {
    if (candidate === null || candidate === undefined || candidate === '') {
      return false;
    }
    return Number.isFinite(Number(candidate));
  });
  return found === undefined ? null : Number(found);
};

const embeddedCardAgents = card => [
  card?.owner,
  card?.assignee,
  card?.assigned_agent,
  card?.item_details?.agent,
];

/**
 * Resolve an agent from the canonical owner id. Embedded payloads are useful
 * only when they describe that same owner; `owner_id: null` is authoritative
 * and must not resurrect an old embedded assignee.
 */
export const resolveCardAgent = (card, availableAgents = []) => {
  const agentId = getCardAgentId(card);
  if (agentId === null) return null;

  const embeddedAgent = embeddedCardAgents(card).find(
    agent => agent && String(agent.id) === String(agentId)
  );
  if (embeddedAgent) return embeddedAgent;

  const agentList = Array.isArray(availableAgents)
    ? availableAgents
    : Object.values(availableAgents || {});
  return agentList.find(agent => String(agent?.id) === String(agentId)) || null;
};

export const normalizeCardStatus = value => {
  const normalized = String(value || '')
    .trim()
    .toLocaleLowerCase();
  return normalized === 'active' ? 'open' : normalized;
};

export const getCardStatus = card => {
  if (card?.status) return normalizeCardStatus(card.status);
  if (card?.won_at) return 'won';
  if (card?.lost_at) return 'lost';
  return normalizeCardStatus(card?.item_details?.status) || 'open';
};

// Exportado: a busca do catálogo de produtos precisa da MESMA normalização que
// o filtro do board — "implantação" tem de casar com "implantacao" nos dois
// lugares, senão o mesmo texto encontra em uma tela e não encontra na outra.
export const normalizeText = value =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();

const cardAgentName = card =>
  card?.owner?.name ||
  card?.assignee?.name ||
  card?.assigned_agent?.name ||
  card?.item_details?.agent?.name ||
  // Legacy agenda payload; the leading underscore is part of its API key.
  // eslint-disable-next-line no-underscore-dangle
  card?.item_details?._agent?.name ||
  '';

/**
 * Shared client-side search used by list and agenda views. Search stays
 * defensive because legacy pipeline payloads legitimately omit description,
 * owner, contact, or stage names.
 */
export const matchesCardSearch = (card, query, stageName = '') => {
  const normalizedQuery = normalizeText(query).trim();
  if (!normalizedQuery) return true;

  const values = [
    card?.title,
    card?.item_details?.title,
    card?.description,
    card?.item_details?.description,
    card?.item_details?.contact_name,
    cardAgentName(card),
    card?.item_details?.conversation?.inbox?.name,
    card?.conversation_display_id,
    card?.item_details?.conversation?.display_id,
    card?.id,
    stageName,
  ];

  return values.some(value => normalizeText(value).includes(normalizedQuery));
};

export const matchesPriorityFilter = (card, priorities) => {
  if (!priorities || priorities.length === 0) return true;
  return priorities.includes(getCardPriority(card));
};

export const matchesValueFilter = (card, range) => {
  const min = range?.min;
  const max = range?.max;
  const hasMin = min !== undefined && min !== null;
  const hasMax = max !== undefined && max !== null;
  if (!hasMin && !hasMax) return true;

  const value = getCardValue(card);
  // Server parity: valueless cards are excluded once a value filter is set.
  if (value === null) return false;
  if (hasMin && value < min) return false;
  if (hasMax && value > max) return false;
  return true;
};

export const matchesAgentFilter = (card, agentId) => {
  // Falsy (null/undefined/0/'') = no agent filter; -1 = "unassigned" sentinel.
  if (!agentId) return true;

  const cardAgentId = getCardAgentId(card);
  if (Number(agentId) === -1) return cardAgentId === null;
  return cardAgentId === Number(agentId);
};

export const matchesStatusFilter = (card, status) => {
  if (!status) return true;
  return getCardStatus(card) === normalizeCardStatus(status);
};

export const matchesStageFilter = (card, stages) => {
  if (!Array.isArray(stages) || stages.length === 0) return true;
  return stages.map(String).includes(String(card?.pipeline_stage));
};

export const matchesSlaFilter = (card, slaExceeded) => {
  if (!slaExceeded) return true;

  const sla = card?.item_details?.sla;
  const stillInSameStage =
    sla &&
    (sla.stage === undefined ||
      String(sla.stage) === String(card?.pipeline_stage));
  return Boolean(sla?.exceeded_at && stillInSameStage);
};

const getCardLabels = card => {
  const labels =
    card?.item_details?.conversation?.label_list ||
    card?.item_details?.label_list ||
    card?.labels ||
    [];

  return Array.isArray(labels)
    ? labels.map(label =>
        typeof label === 'string' ? label : label?.title || label?.name
      )
    : [];
};

export const matchesLabelsFilter = (card, labels) => {
  if (!Array.isArray(labels) || labels.length === 0) return true;
  const cardLabels = getCardLabels(card).map(normalizeText);
  return labels.every(label => cardLabels.includes(normalizeText(label)));
};

const DATE_ONLY_REGEXP = /^(\d{4})-(\d{2})-(\d{2})$/;

const validDateBound = value => {
  const match = String(value || '').match(DATE_ONLY_REGEXP);
  if (!match) return null;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10) === value ? value : null;
};

export const matchesDateFilter = (
  card,
  dateRange,
  reportingTimezone = 'UTC'
) => {
  if (!dateRange || (!dateRange.start && !dateRange.end)) return true;

  const cardDate =
    formatInReportingTimezone(
      card?.created_at,
      'yyyy-MM-dd',
      reportingTimezone
    ) || formatInReportingTimezone(card?.created_at, 'yyyy-MM-dd', 'UTC');
  if (!cardDate) return false;

  if (dateRange.start) {
    const startDate = validDateBound(dateRange.start);
    if (startDate && cardDate < startDate) return false;
  }
  if (dateRange.end) {
    const endDate = validDateBound(dateRange.end);
    if (endDate && cardDate > endDate) return false;
  }
  return true;
};

export const matchesColumnFilters = (
  card,
  filters,
  { reportingTimezone = 'UTC' } = {}
) => {
  if (!filters) return true;
  return (
    matchesPriorityFilter(card, filters.priority) &&
    matchesValueFilter(card, filters.value) &&
    matchesAgentFilter(card, filters.agent_id) &&
    matchesDateFilter(card, filters.date, reportingTimezone) &&
    matchesStatusFilter(card, filters.status) &&
    matchesStageFilter(card, filters.stages) &&
    matchesSlaFilter(card, filters.sla_exceeded) &&
    matchesLabelsFilter(card, filters.labels)
  );
};
